/**
 * EDGE AUTO-SCROLL FOR THE PIPELINE BOARD'S DRAG (2026-09-22).
 *
 * THE BUG THIS EXISTS FOR. The board is eleven 280px lanes inside ONE
 * horizontally-scrolling box (`pipeline-board-scroll`), so on any ordinary
 * screen the last lanes are off the right-hand edge. Native HTML5 drag gives
 * you NOTHING here: while a drag is in progress the browser does not scroll a
 * container for you, the mouse wheel is swallowed, and you cannot let go to
 * scroll without dropping the card. So a lane you cannot see is a lane you
 * cannot drop on — which is how 68 cards came to be stuck in `plan_sent` with
 * `unreachable`, the LAST column, unreachable by drag.
 *
 * ⚠️ IT IS A REACHABILITY FIX, NOT THE FEATURE. The drawer's ⋯ menu is the
 * primary route and works with no dragging at all (and is the only route for a
 * keyboard, which cannot start a native drag). This makes the obvious gesture
 * work as well; it must never become the only way to make the move.
 *
 * WHY A PURE FUNCTION. jsdom does no layout and fires no real drag, so the rAF
 * loop and the listeners in `PipelineBoard` cannot be unit-tested in this repo
 * (the lesson the lane-footer geometry learned the hard way — see
 * `laneMoney.spec`). The DECISION is therefore all here, taking plain numbers
 * and returning one, so every rule below is testable without a DOM: what is
 * left in the component is one `scrollLeft +=`.
 */

/**
 * How close to an edge the pointer must come before the board starts moving.
 *
 * A lane is 280px, so this is about a quarter of one — wide enough to hit
 * without precision while dragging a card, narrow enough that crossing the
 * board normally never triggers it.
 */
export const EDGE_ZONE_PX = 72;

/**
 * The fastest we will move in a single animation frame (~60fps → ~1440px/s at
 * the very edge). Fast enough to cross the whole board in about a second,
 * slow enough that the lane you want does not shoot past you.
 */
export const MAX_STEP_PX = 24;

export interface EdgeScrollInput {
  /** The pointer's viewport X — `DragEvent.clientX`. */
  pointerX: number;
  /** The scroll container's viewport edges — `getBoundingClientRect()`. */
  left: number;
  right: number;
  /** Where it is scrolled to, and the furthest it can go (`scrollWidth - clientWidth`). */
  scrollLeft: number;
  maxScrollLeft: number;
}

/**
 * How far to scroll the board this frame: negative = left, positive = right,
 * 0 = leave it alone.
 *
 * THE RULES, each one a thing that would otherwise be wrong:
 *
 *  - **Nothing happens outside the two edge zones.** Dragging a card between
 *    two middle lanes must not make the board move under it.
 *  - **The ramp is proportional**, so the board creeps as you enter the zone
 *    and only reaches full speed at the very edge. A fixed step makes the
 *    board feel like it lurches the moment you approach a lane.
 *  - **It stops dead at both ends.** At `scrollLeft === 0` a leftward step is
 *    0, not a negative number the container would clamp silently — the caller
 *    uses a zero to park its animation loop, so inventing motion nobody can
 *    see would spin a rAF loop forever.
 *  - **A board that does not scroll never scrolls.** `maxScrollLeft <= 0`
 *    means every lane is already visible (a wide screen, or a filtered board),
 *    and the answer is always 0.
 *  - **A pointer outside the container still counts** if it is within the zone
 *    measured from the edge — a drag that has wandered just past the board's
 *    right edge is the clearest possible signal that somebody wants to go
 *    further right.
 */
export function edgeScrollStep(input: EdgeScrollInput): number {
  const { pointerX, left, right, scrollLeft, maxScrollLeft } = input;
  if (!(maxScrollLeft > 0)) return 0;

  const fromLeft = pointerX - left;
  const fromRight = right - pointerX;

  // ⚠️ RIGHT EDGE WINS A TIE (`<=`, not `<`). On a container narrower than two
  // zones both claims overlap, and with a strict `<` a pointer exactly halfway
  // falls through to the LEFT branch — so the board runs backwards, away from
  // the lane somebody is trying to reach, on precisely the narrow viewport
  // that needs this most. The bias is toward the right because that is where
  // the lanes you cannot see are.
  if (fromRight < EDGE_ZONE_PX && fromRight <= fromLeft) {
    if (scrollLeft >= maxScrollLeft) return 0;
    const step = Math.ceil(ramp(fromRight) * MAX_STEP_PX);
    return Math.min(step, maxScrollLeft - scrollLeft);
  }
  if (fromLeft < EDGE_ZONE_PX) {
    if (scrollLeft <= 0) return 0;
    const step = Math.ceil(ramp(fromLeft) * MAX_STEP_PX);
    return -Math.min(step, scrollLeft);
  }
  return 0;
}

/**
 * 0 at the outer boundary of the zone → 1 at the edge (and beyond it, for a
 * pointer that has left the container entirely). Clamped, so a pointer a long
 * way outside cannot ask for a step larger than `MAX_STEP_PX`.
 */
function ramp(distanceFromEdge: number): number {
  const depth = (EDGE_ZONE_PX - distanceFromEdge) / EDGE_ZONE_PX;
  return Math.min(1, Math.max(0, depth));
}
